import { cn } from '@/lib/utils';
import type { TodayJobRow } from '../api';

interface Props {
  jobs: TodayJobRow[];
  total: number;
  loading?: boolean;
  language?: 'sv' | 'en';
}

const STATUS_DOT: Record<string, string> = {
  completed: '#1B7F4D',
  approved: '#1B7F4D',
  cancelled: '#C24138',
  assigned: '#607C6E',
};

export function TodayRail({ jobs, total, loading, language = 'sv' }: Props) {
  const label = language === 'sv' ? 'IDAG' : 'TODAY';
  const overflow = Math.max(total - jobs.length, 0);

  return (
    <div className="cmd-panel cmd-panel-inner rounded p-3">
      <div className="flex items-center gap-3 flex-wrap">
        {/* Label */}
        <span className="cmd-label shrink-0">{label}</span>

        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-7 w-40 rounded-full animate-pulse"
              style={{ background: 'rgba(7,51,49,0.05)' }}
            />
          ))
        ) : jobs.length === 0 ? (
          <span className="text-xs font-mono text-[#607C6E]">
            {language === 'sv' ? 'Inga jobb idag.' : 'No jobs today.'}
          </span>
        ) : (
          <>
            {jobs.map((job) => {
              const unassigned = !job.cleaner_name;
              const dotColor = STATUS_DOT[job.status] ?? '#607C6E';
              const siteName =
                job.site_name ?? (language === 'sv' ? 'Okänt objekt' : 'Unknown site');

              return (
                <button
                  key={job.id}
                  type="button"
                  onClick={() => {
                    if (job.site_id) {
                      window.location.hash = `#/brfs/${job.site_id}?tab=jobs`;
                    }
                  }}
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-mono transition-colors border',
                    'text-[#2E4640] hover:text-[#073331]',
                    unassigned
                      ? 'bg-[#B97E1F]/10 border-[#B97E1F]/35 hover:bg-[#B97E1F]/20 hover:border-[#B97E1F]/60'
                      : 'bg-[#073331]/[0.04] border-[#073331]/10 hover:bg-[#073331]/[0.08] hover:border-[#073331]/30',
                  )}
                >
                  {/* Status dot */}
                  <div
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: dotColor }}
                  />

                  {/* Site name */}
                  <span className="max-w-[120px] truncate">{siteName}</span>

                  {/* Cleaner or unassigned */}
                  <span
                    className="shrink-0 text-[10px]"
                    style={{ color: unassigned ? '#B97E1F' : '#607C6E' }}
                  >
                    {unassigned
                      ? language === 'sv' ? 'EJ TILLDELAD' : 'UNASSIGNED'
                      : job.cleaner_name}
                  </span>
                </button>
              );
            })}

            {/* Overflow chip — muted, non-interactive */}
            {overflow > 0 && (
              <span
                className="flex items-center px-2.5 py-1.5 rounded-full text-xs font-mono text-[#607C6E] border border-[#073331]/[0.08]"
                style={{ background: 'rgba(7,51,49,0.03)' }}
              >
                +{overflow} {language === 'sv' ? 'till' : 'more'}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
